// Build trigger: rebuild forced for commits that touch only files
// outside the conditional-build's path filter (nginx.conf,
// docker-entrypoint.sh, etc.). The frontend matrix in
// .github/workflows/hoteliate-cicd.yaml has no `watch_paths` array,
// so the path filter defaults to a subset that doesn't catch
// non-src config changes — the build silently skips and the broken
// image stays live.
//
// Last forced rebuild: 914855ac (revert IP-pinning entrypoint
// resolve loop that was crashlooping pods). Touching this file is
// the cheapest way to give the path filter a hit.
//
// Proper fix lives in the workflow file; can't be edited from this
// OAuth context (needs `workflow` scope).
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import App from './App.tsx'
import '@fontsource-variable/inter'
import '@fontsource-variable/heebo'
import '@fontsource-variable/fraunces'
import './index.css'
import './i18n/config' // Import i18n configuration
import { logger } from './utils/logger'
// Validate environment variables on startup
import { validateEnv, printEnvErrors } from './config/env'

// Run environment validation and log results
const envValidation = validateEnv()
if (!envValidation.isValid || envValidation.warnings.length > 0) {
  printEnvErrors(envValidation)
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // #314: avoid refetching on every component mount. 60s is long enough
      // to deduplicate tab-to-tab navigation but short enough that stale data
      // doesn't linger; mutations still invalidate explicitly.
      staleTime: 60_000,
    },
  },
})

// Stale-chunk recovery: when a deploy ships new content-hashed lazy
// chunks, in-flight sessions still hold the previous index.html which
// references chunk names that no longer exist on the new pods. The
// browser's dynamic import 404s — visible as nginx 'No such file' on
// /assets/<Page>-<hash>.js. Force-reload the page so the SPA pulls the
// fresh manifest. SessionStorage gate prevents infinite reload loops
// if the failure is actually a persistent CDN/network issue.
const STALE_CHUNK_REGEX = /(Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module)/i
const RELOAD_KEY = 'hoteliate:stale-chunk-reload-at'
const reloadOnceForStaleChunk = (sourceMsg: string) => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
  if (Date.now() - last < 60_000) {
    logger.warn('Stale chunk: skipping reload (already reloaded <60s ago)', { sourceMsg })
    return
  }
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  logger.info('Stale chunk detected — reloading page to fetch fresh bundle', { sourceMsg })
  window.location.reload()
}
window.addEventListener('unhandledrejection', (e) => {
  const msg = String((e.reason && (e.reason.message || e.reason)) || '')
  if (STALE_CHUNK_REGEX.test(msg)) reloadOnceForStaleChunk(msg)
})
// Exposed so ErrorBoundary can call the same gated reload when React
// surfaces a chunk-load error via componentDidCatch.
;(window as any).__reloadOnStaleChunk = (msg: string) => {
  if (STALE_CHUNK_REGEX.test(msg)) reloadOnceForStaleChunk(msg)
}
// Vite's canonical hook: fired on window whenever a dynamically-imported
// chunk fails to load (the exact post-deploy stale-hash 404 we saw at the
// 12:49 roll — nginx served 404 for old /assets/<Page>-<hash>.js). This is
// the reliable path: the two handlers above only fire when the failure
// surfaces as an unhandled rejection or a React render error whose message
// matches STALE_CHUNK_REGEX, which misses import()s in event handlers, MIME
// -type variants, and preloads that never reach an ErrorBoundary. This event
// fires for ALL of them, so it closes the gap that left in-flight sessions
// stuck (visible as "affiliates can't approve"). No regex gate needed — a
// preloadError is by definition a failed chunk; the 60s cooldown in
// reloadOnceForStaleChunk still prevents reload loops on a persistent outage.
window.addEventListener('vite:preloadError', (event) => {
  const err = (event as { payload?: Error }).payload
  reloadOnceForStaleChunk(`vite:preloadError: ${err?.message ?? ''}`)
})

// Log application startup
logger.info('Frontend application starting', {
  userAgent: navigator.userAgent,
  url: window.location.href,
  timestamp: new Date().toISOString(),
  environment: import.meta.env?.MODE || 'development',
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        <Toaster
          position="bottom-right"
          gutter={20}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#212121',
              color: '#fff',
              padding: '14px 18px',
              borderRadius: '10px',
              fontSize: '15px',
              lineHeight: 1.5,
              fontWeight: 500,
              maxWidth: '400px',
              minWidth: '280px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.1)',
            },
            success: {
              style: {
                background: '#43a047',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#43a047',
              },
            },
            error: {
              style: {
                background: '#e53935',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#e53935',
              },
            },
            loading: {
              style: {
                background: '#212121',
                color: '#fff',
              },
              iconTheme: {
                primary: '#fff',
                secondary: '#616161',
              },
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
)